import { closeHistory } from '@tiptap/pm/history';
import { Fragment } from '@tiptap/pm/model';
import { contentEditorSelectionLocks } from './contentEditorSelectionLocks.js';

export function findContentEditorTable(editor) {
  if (!editor) return null;
  const { selection } = editor.state;
  if (selection.node?.type.name === 'table') return { node: selection.node, position: selection.from, row: 0, column: 0 };
  const { $from } = selection;
  for (let depth = $from.depth; depth > 0; depth--) {
    if ($from.node(depth).type.name === 'table') return { node: $from.node(depth), position: $from.before(depth), row: $from.index(depth), column: $from.depth > depth + 1 ? $from.index(depth + 1) : 0 };
  }
  return null;
}

export function updateContentEditorTable(editor, action) {
  const table = findContentEditorTable(editor);
  if (!editor?.isEditable || !table) return false;
  const { node, position, row, column } = table;
  // Row/column operations on merged cells require a table-map implementation.
  let merged = false;
  node.descendants(cell => { if (['tableCell', 'tableHeader'].includes(cell.type.name) && (cell.attrs.colspan > 1 || cell.attrs.rowspan > 1)) merged = true; });
  if (merged) return false;
  const rows = Array.from({ length: node.childCount }, (_, index) => node.child(index));
  const blank = type => type.create(null, editor.schema.nodes.paragraph.create());
  if (action === 'add-row') rows.splice(row + 1, 0, editor.schema.nodes.tableRow.create(null, Array.from({ length: rows[row].childCount }, () => blank(editor.schema.nodes.tableCell))));
  else if (action === 'delete-row') { if (rows.length <= 1) return false; rows.splice(row, 1); }
  else if (action === 'add-column' || action === 'delete-column') {
    if (action === 'delete-column' && rows.some(item => item.childCount <= 1)) return false;
    rows.forEach((item, index) => {
      const cells = Array.from({ length: item.childCount }, (_, i) => item.child(i));
      if (action === 'add-column') cells.splice(column + 1, 0, blank(cells[Math.min(column, cells.length - 1)].type));
      else cells.splice(column, 1);
      rows[index] = item.copy(Fragment.from(cells));
    });
  } else if (action === 'header') {
    const type = rows[0].firstChild.type.name === 'tableHeader' ? editor.schema.nodes.tableCell : editor.schema.nodes.tableHeader;
    rows[0] = rows[0].copy(Fragment.from(Array.from({ length: rows[0].childCount }, (_, index) => { const cell = rows[0].child(index); return type.create(cell.attrs, cell.content); })));
  } else return false;
  const original = editor.state.doc;
  editor.view.dispatch(closeHistory(editor.state.tr.replaceWith(position, position + node.nodeSize, node.copy(Fragment.from(rows)))));
  editor.commands.focus();
  return editor.state.doc !== original;
}

export function ContentEditorTableTools({ editor, profile }) {
  const table = findContentEditorTable(editor);
  if (!table) return null;
  const locks = contentEditorSelectionLocks(editor, table, profile);
  if (profile.mode !== 'template') table.node.descendants(node => { if (node.attrs.lockId && node.attrs.blockLock?.edit) locks.edit = true; });
  let merged = false;
  table.node.descendants(cell => { if (cell.attrs.colspan > 1 || cell.attrs.rowspan > 1) merged = true; });
  return <div role="group" aria-label="Table tools" className="mts-editor-table-tools">
    {[['add-row', 'Add row'], ['delete-row', 'Delete row'], ['add-column', 'Add column'], ['delete-column', 'Delete column'], ['header', 'Toggle header row']].map(([action, label]) => <button key={action} type="button" disabled={!editor.isEditable || locks.edit || locks.remove || merged || action === 'delete-row' && table.node.childCount <= 1 || action === 'delete-column' && table.node.firstChild.childCount <= 1} title={merged ? 'Row and column tools are unavailable for merged cells.' : label} onMouseDown={event => event.preventDefault()} onClick={() => updateContentEditorTable(editor, action)}>{label}</button>)}
  </div>;
}
